import React from "react";
import { assetUrl } from "../../lib/buildSnapshot";

/**
 * ResponsiveImage — high-performance image renderer.
 *
 * Auto-detects uploaded multi-size WebP assets from Batch E
 * (path pattern: .../uploads/{cat}/{uuid}/medium.webp) and produces
 * a <picture> element with a srcset spanning thumbnail (400w),
 * medium (800w), full (1600w) so browsers download the smallest
 * variant that fits the layout.
 *
 * Props:
 *   src        - primary image URL (medium.webp for uploads, or any URL)
 *   alt        - required, meaningful alt text
 *   priority   - true → loading="eager", fetchpriority="high" (hero images only)
 *   sizes      - CSS sizes attribute (defaults tuned for card grids)
 *   width/height - intrinsic dimensions (prevents CLS)
 *   className  - passed through
 *   fallback   - src used if the primary errors out (SVG data URI etc.)
 *   aspectRatio - override for aspect-ratio CSS (e.g. "16/9")
 */
export default function ResponsiveImage({
    src,
    alt = "",
    priority = false,
    sizes,
    width,
    height,
    className = "",
    fallback,
    aspectRatio,
    ...rest
}) {
    const variants = React.useMemo(() => buildVariants(src), [src]);
    const [errored, setErrored] = React.useState(false);
    const effectiveSrc = errored && fallback ? fallback : (variants?.medium || src);
    const effectiveSrcSet =
        !errored && variants
            ? `${variants.thumbnail} 400w, ${variants.medium} 800w, ${variants.full} 1600w`
            : undefined;

    const commonProps = {
        alt,
        src: assetUrl(effectiveSrc),
        srcSet: effectiveSrcSet,
        sizes: sizes || (variants ? "(max-width: 640px) 400px, (max-width: 1024px) 800px, 1200px" : undefined),
        loading: priority ? "eager" : "lazy",
        decoding: priority ? "sync" : "async",
        fetchPriority: priority ? "high" : "auto",
        width: width || undefined,
        height: height || undefined,
        className,
        onError: () => setErrored(true),
        style: aspectRatio ? { aspectRatio, ...(rest.style || {}) } : rest.style,
        ...rest,
    };

    // Use <picture> only when we have real variants; keeps DOM lean otherwise.
    if (!variants) return <img {...commonProps} />;

    return (
        <picture>
            <source type="image/webp" srcSet={effectiveSrcSet} sizes={commonProps.sizes} />
            <img {...commonProps} />
        </picture>
    );
}

/**
 * If the URL follows the Batch-E upload pattern (…/{uuid}/medium.webp),
 * derive the sibling thumbnail.webp / full.webp URLs. Otherwise return null.
 */
function buildVariants(src) {
    if (!src || typeof src !== "string") return null;
    const match = src.match(/^(.*\/uploads\/[^/]+\/[^/]+)\/(thumbnail|medium|full)\.webp(\?.*)?$/);
    if (!match) return null;
    const base = match[1];
    const q = match[3] || "";
    return {
        thumbnail: `${base}/thumbnail.webp${q}`,
        medium: `${base}/medium.webp${q}`,
        full: `${base}/full.webp${q}`,
    };
}
